import { ReviewersBatchItem } from '../../components/ReviewersEdit/ReviewersEdit.utils';
import { downloadFile, get, post, postUploadFile, put } from '../apiRequest';

export enum ActionStatus {
  ScheduledClaim = 'ScheduledClaim',
  FiledClaim = 'FiledClaim',
  Categorized = 'Categorized',
  MatchingComplete = 'MatchingComplete',
  MatchesFound = 'MatchesFound',
  Matched = 'MatchCodeAssigned',
  Assigned = 'Assigned',
  ClientReconciled = 'ClientReconciled',
  AlixReconciled = 'AlixReconciled',
  ObjectionPending = 'ObjectionPending',
  ObjectionFiled = 'ObjectionFiled',
  ObjectionContinued = 'ObjectionContinued',
  ObjectionOrderedPartial = 'ObjectionOrderedPartial',
  ObjectionOrderedFinal = 'ObjectionOrderedFinal',
  ObjectionWithdrawn = 'ObjectionWithdrawn',
  Withdrawn = 'Withdrawn',
  SettlementPending = 'SettlementPending',
  SettlementPartial = 'SettlementPartial',
  SettlementFinal = 'SettlementFinal',
  Voided = 'Voided',
  TrumpedSchedule = 'TrumpedSchedule',
  ZeroDollarValueScheduledOnly = 'ZeroDollarValueScheduledOnly',
  Allowable = 'Allowable',
  Allowed = 'Allowed',
  OrderVacated = 'OrderVacated',
  SettlementResolved = 'SettlementResolved',
}

export interface ClaimRow {
  id: number;
  actionName?: string;
  actionStatus?: ActionStatus;
  actionSubStatusName?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  barDateRef?: string;
  city?: string;
  claimId?: number;
  claimImage?: string;
  claimReviewers?: string;
  claimsAgentNumber?: string;
  claimScheduleNumber?: string;
  clientAdministrative?: number;
  clientContingent?: boolean;
  clientDebtor?: string;
  clientDisputed?: boolean;
  clientPriority?: number;
  clientSecured?: number;
  clientTotal?: number;
  clientUndetermined?: boolean;
  clientUnliquidated?: boolean;
  clientUnsecured?: number;
  counselAdministrative?: number;
  counselContingent?: boolean;
  counselDebtor?: string;
  counselDisputed?: boolean;
  counselPriority?: number;
  counselSecured?: number;
  counselTotal?: number;
  counselUndetermined?: boolean;
  counselUnliquidated?: boolean;
  counselUnsecured?: number;
  counterpartyName: string;
  country?: string;
  createdDate?: string;
  currentAdministrative?: number;
  currentContingent?: boolean;
  currentDebtor?: string;
  currentDisputed?: boolean;
  currentPriority?: number;
  currentSecured?: number;
  currentTotal?: number;
  currentUndetermined?: boolean;
  currentUnliquidated?: boolean;
  currentUnsecured?: number;
  dateRecdFromClaimsAgent?: string;
  email?: string;
  hasOneHundredPercentMatch?: boolean;
  highAdministrative?: number;
  highPriority?: number;
  highSecured?: number;
  highTotal?: number;
  highUnsecured?: number;
  lowAdministrative?: number;
  lowPriority?: number;
  lowSecured?: number;
  lowTotal?: number;
  lowUnsecured?: number;
  majorCategory?: string;
  matchCode: number;
  minorCategory?: string;
  motionMajorCategory?: string;
  motionMinorCategory?: string;
  notMatched?: boolean;
  numberOfMatches?: number;
  objectionCommentary?: string;
  officialClaimDate?: string;
  oneHundredPercentMatchCode?: string;
  originalAdministrative?: number;
  originalContingent?: boolean;
  originalCurrentProposed?: string;
  originalDebtor?: string;
  originalDisputed?: boolean;
  originalSecured?: number;
  originalTotal?: number;
  originalUndetermined?: boolean;
  originalUnliquidated?: boolean;
  originalUnsecured?: number;
  parentClaimNumber?: string;
  phone?: string;
  pORAdministrative?: number;
  pORContingent?: boolean;
  pORDebtor?: string;
  pORDisputed?: boolean;
  pORPriority?: number;
  pORSecured?: number;
  pORTotal?: number;
  pORUndetermined?: boolean;
  pORUnliquidated?: boolean;
  pORUnsecured?: number;
  proposedAdministrative?: number;
  proposedContingent?: boolean;
  proposedDebtor?: string;
  proposedDisputed?: boolean;
  proposedPriority?: number;
  proposedSecured?: number;
  proposedTotal?: number;
  proposedUndetermined?: boolean;
  proposedUnliquidated?: boolean;
  proposedUnsecured?: number;
  redactImage?: boolean;
  redactNameAddress?: boolean;
  referenceNumber: string;
  resolutionTypeId?: number;
  resolutionTypeName?: string;
  reviewLevel?: string;
  reviewLevelId?: number;
  scheduledDescription?: string;
  sourceType?: string;
  state?: string;
  subMatchCode?: number;
  survivingClaimNumber?: string;
  underSeal?: boolean;
  zip?: string;
}

export interface ClaimBatchData {
  claimIds: number[];
  values: Partial<{
    survivingClaimNumber: string;
    counterpartyName: string;
    categoryId: number;
    actionStatus: string;
    actionReview: string;
    actionNameId: number;
    alixPartnersDebtorId: number;
    proposedDebtorId: number;
    clientDebtorId: number;
    counselDebtorId: number;
    alixPartnersSecured: number;
    alixPartnersAdministrative: number;
    alixPartnersUnsecured: number;
    alixPartnersPriority: number;
    alixPartnersTotal: number;
    proposedSecured: number;
    proposedAdministrative: number;
    proposedUnsecured: number;
    proposedPriority: number;
    proposedTotal: number;
    clientSecured: number;
    clientAdministrative: number;
    clientUnsecured: number;
    clientPriority: number;
    clientTotal: number;
    counselSecured: number;
    counselAdministrative: number;
    counselUnsecured: number;
    counselPriority: number;
    counselTotal: number;
    objectionCommentary: string;
    comment: string;
    claimImageUrl: string;
    notes: string;
    settlementNumber: string;
    claimReviewerUserNames: string[];
    claimReviewerUserGroupIds: number[];
  }>;
}

export type ClaimEditChangeOptionsMarkFlags = {
  markClaimAsReconciled?: boolean;
  markClaimReviewAsComplete?: boolean;
  markClaimAsAssigned?: boolean;
  markClaimAsCategorized?: boolean;
  markClaimAsReviewComplete?: boolean;
  markClaimAsFiled?: boolean;
  markClaimAsOrdered?: boolean;
  markClaimAsSettledAndResolved?: boolean;
};

export type ClaimEditChangeOptions = {
  comment: string;
} & ClaimEditChangeOptionsMarkFlags;

export type LabeledClaimEditChangeOptionsMarkFlag = {
  label: string;
  changeOptionsMarkFlag: keyof ClaimEditChangeOptionsMarkFlags;
};

export interface ClaimEditData {
  claimId: number | undefined;
  values: ClaimEditValues;
  changeOptions: ClaimEditChangeOptions;
}

export type ClaimEditValues = {
  id: number;
  debtorId?: number;
  originalDebtorId?: number;
  currentDebtorId?: number;
  proposedDebtorId?: number;
  alixPartnersDebtorId?: number;
  clientDebtorId?: number;
  counselDebtorId?: number;
  pORDebtorId?: number;
  claimReviewer: ReviewersBatchItem;
  currentSecured?: number;
  currentAdministrative?: number;
  currentPriority?: number;
  currentUnsecured?: number;
  currentTotal?: number;
  dashboardCategory?: string;
  proposedSecured?: number;
  proposedAdministrative?: number;
  proposedPriority?: number;
  proposedUnsecured?: number;
  proposedTotal?: number;
  alixPartnersSecured?: number;
  alixPartnersAdministrative?: number;
  alixPartnersPriority?: number;
  alixPartnersUnsecured?: number;
  alixPartnersTotal?: number;
  clientSecured?: number;
  clientAdministrative?: number;
  clientPriority?: number;
  clientUnsecured?: number;
  clientTotal?: number;
  counselSecured?: number;
  counselAdministrative?: number;
  counselPriority?: number;
  counselUnsecured?: number;
  counselTotal?: number;
  highSecured?: number;
  highAdministrative?: number;
  highPriority?: number;
  highUnsecured?: number;
  highTotal?: number;
  lowSecured?: number;
  lowAdministrative?: number;
  lowPriority?: number;
  lowUnsecured?: number;
  lowTotal?: number;
  PORSecured?: number;
  PORAdministrative?: number;
  PORPriority?: number;
  PORUnsecured?: number;
  PORTotal?: number;
  parentClaimNumber?: string;

  currentContingent?: boolean;
  currentUnliquidated?: boolean;
  currentUndetermined?: boolean;
  currentDisputed?: boolean;
  proposedContingent?: boolean;
  proposedUnliquidated?: boolean;
  proposedUndetermined?: boolean;
  proposedDisputed?: boolean;
  alixPartnersContingent?: boolean;
  alixPartnersUnliquidated?: boolean;
  alixPartnersUndetermined?: boolean;
  alixPartnersDisputed?: boolean;
  clientContingent?: boolean;
  clientUnliquidated?: boolean;
  clientUndetermined?: boolean;
  clientDisputed?: boolean;
  counselContingent?: boolean;
  counselUnliquidated?: boolean;
  counselUndetermined?: boolean;
  counselDisputed?: boolean;
  PORContingent?: boolean;
  PORUnliquidated?: boolean;
  PORUndetermined?: boolean;
  PORDisputed?: boolean;

  majorCategoryId?: number;
  minorCategoryId?: number;

  customString01: string;
  customString02: string;
  customString03: string;
  customString04: string;
  customString05: string;
  customString06: string;
  customString07: string;
  customString08: string;
  customString09: string;
  customString10: string;
  customString11: string;
  customString12: string;
  customString13: string;
  customString14: string;
  customString15: string;
  customDecimal01?: number;
  customDecimal02?: number;
  customDecimal03?: number;
  customDecimal04?: number;
  customDecimal05?: number;
  customDecimal06?: number;
  customDecimal07?: number;
  customDecimal08?: number;
  customDecimal09?: number;
  customDecimal10?: number;
  customDecimal11?: number;
  customDecimal12?: number;
  customDecimal13?: number;
  customDecimal14?: number;
  customDecimal15?: number;

  customBoolean01?: boolean;
  customBoolean02?: boolean;
  customBoolean03?: boolean;
  customBoolean04?: boolean;
  customBoolean05?: boolean;

  customDateTime01?: Date;
  customDateTime02?: Date;
  customDateTime03?: Date;
  customDateTime04?: Date;
  customDateTime05?: Date;

  referenceNumber: string;
  actionStatus: ActionStatus;
  actionReview: string;
  counterpartyName: string;
  matchCode: number;
  subMatchCode: number;
  numberOfMatches?: number;
  notMatched: boolean;
  hasOneHundredPercentMatch: boolean;
  oneHundredPercentMatchCode?: string;
  createdDate?: string;
  sourceType?: string;
  majorCategory?: string;
  minorCategory?: string;
  currentDebtor?: string;
  objectionCommentary?: string;
  notes: string;
  settlementNumber: string;
  pocSupport: boolean;
  matchCurrentPrepetitionOpenAp: string;

  // ClaimRow fields
  contactFullName?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  redactNameAddress?: boolean;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  email?: string;
  phone?: string;
  originalDebtor?: string;
  proposedDebtor?: string;
  clientDebtor?: string;
  counselDebtor?: string;
  pORDebtor?: string;
  barDateRef?: string;
  claimScheduleNumber?: string;
  scheduledDescription?: string;
  claimsAgentNumber?: string;
  redactImage?: boolean;
  claimImage?: string;
  underSeal?: boolean;
  officialClaimDate?: string;
  dateRecdFromClaimsAgent?: string;
  originalSecured?: number;
  originalAdministrative?: number;
  originalUnsecured?: number;
  originalTotal?: number;
  pORSecured?: number;
  pORAdministrative?: number;
  pORPriority?: number;
  pORUnsecured?: number;
  pORTotal?: number;
  originalContingent?: boolean;
  originalUnliquidated?: boolean;
  originalDisputed?: boolean;
  originalUndetermined?: boolean;
  pORContingent?: boolean;
  pORUnliquidated?: boolean;
  pORDisputed?: boolean;
  pORUndetermined?: boolean;
  reviewLevel?: number;
  reviewLevelId?: number;
  survivingClaimNumber?: string;

  claimReviewerUserGroupIds: number[];
  claimReviewerUserIds: number[];

  // resolution types
  resolutionTypeId?: number;
  actionSubStatusId?: number;
  actionSubStatusName?: string;
  actionNameId?: number;
  actionName?: string;

  _fetchTimestamp?: string;
};

export type ClaimEditField = keyof ClaimEditValues;

export enum SourceType {
  FiledClaim = 'FiledClaim',
  ScheduledClaim = 'ScheduledClaim',
}

export interface CounterpartySummaryAmounts {
  sourceType: SourceType;
  count: number;
  originalSecured: number;
  originalAdmin: number;
  originalPriority: number;
  originalUnsecured: number;
  originalTotal: number;
  currentSecured: number;
  currentAdmin: number;
  currentPriority: number;
  currentUnsecured: number;
  currentTotal: number;
  proposedSecured: number;
  proposedAdmin: number;
  proposedPriority: number;
  proposedUnsecured: number;
  proposedTotal: number;
}

export interface CounterpartySummary {
  matchCode: number;
  counterPartyName: string;
  counterpartySummaryAmounts: CounterpartySummaryAmounts[];
}

export type ClaimBatchEditResponse = {
  id: number;
  status: ClaimBatchEditStatus;
  claimsUpdated: number;
};

export enum ClaimBatchEditStatus {
  Failed = 'Failed',
  Success = 'Success',
  Pending = 'Pending',
}

export type BatchEditPostBackground = {
  displayName: string;
  fileName: string;
  directoryName: string;
  containerName: string;
};

export enum BatchEditBackgroundResponseType {
  Success = 1,
  Failed = 2,
}

export type BatchEditBackgroundResponse = {
  id: number;
  status: 'InProgess' | 'Completed' | 'Failed' | 'Queued';
  data: {
    claimsUpdated?: number;
    contractsUpdated?: number;
    id: number;
    status: BatchEditBackgroundResponseType;
  };
};

export interface ClaimResolutionType {
  resolutionTypeId: number;
  resolutionTypeName: string;
  resolutionSubTypes: ClaimResolutionSubType[];
}

export interface ClaimResolutionSubType {
  hearingDateRequired: boolean;
  actionSubStatusId: number;
  actionSubStatusName: string;
  workflowStatuses: ClaimWorkflowStatus[];
}

export interface ClaimWorkflowStatus {
  actionStatusId: number;
  actionStatus: ActionStatus;
  actionStatusDisplayName: string;
}

export const claims = {
  getCounterpartySummary: (matchCode: number) =>
    get<CounterpartySummary>(`v1/CounterpartySummary/${matchCode}`),
  edit: (data: ClaimEditData) => put('v1/Claims/edit', data),
  warnings: (data: ClaimEditData) => post<string[]>('v1/Claims/warnings', data),
  batchEdit: (data: ClaimBatchData) => post('v1/Claims/batchEdit', data),
  postBatchEditDoc: async (data: FormData) =>
    postUploadFile<ClaimBatchEditResponse>('v1/Claims/batchEditUpload', data),
  downloadErrorLogs: async (claimBatchEditFileId: number) =>
    await downloadFile(
      `v1/Claims/batchEditUploadErrors?claimBatchUpdateFileId=${claimBatchEditFileId}`,
      'ErrorLog.xlsx',
    ),
  batchEditUploadBackground: async (data: BatchEditPostBackground) =>
    post<{ jobId: number; statusEndpoint: string }>(
      'v1/Claims/batchEditUpload/background',
      data,
    ),
  resolutionTypes: () => get<ClaimResolutionType[]>('v1/Claims/resolutiontypes'),
};
